import { getActiveOrgId } from './tenant';

// Super-admin impersonation ("view as user"): while a session is active, every
// request carries the impersonation token so the backend serves the target
// member's data. Read from sessionStorage directly to avoid a circular import.
export const IMPERSONATE_TOKEN_KEY = 'crest_impersonate_token';
function impersonateHeaders(): Record<string, string> {
  try {
    const t =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(IMPERSONATE_TOKEN_KEY) : null;
    return t ? { 'x-impersonate-token': t } : {};
  } catch {
    return {};
  }
}

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
export const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);

export const API_ENDPOINTS = {
  // Search endpoints
  searchMembers: '/api/search/members',
  searchSuggestions: '/api/search/suggestions',
  searchFilters: '/api/search/filters',

  // Admin endpoints (for profile updates that need OpenSearch sync)
  updateMemberEmail: (memberId: string) => `/api/admin/members/${memberId}/email`,
  syncMember: (memberId: string) => `/api/admin/members/${memberId}/sync`,

  // Auth endpoints
  verifyToken: '/api/auth/verify',
};

// Helper function to get current user token
export async function getCurrentUserToken(): Promise<string | null> {
  // Import dynamically to avoid circular dependency
  const { authService } = await import('../services/authService');
  return authService.getIdToken();
}

// Generic API request helper
export async function apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getCurrentUserToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-organization-id': getActiveOrgId(),
      ...impersonateHeaders(),
  };

  if (options?.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        headers[key] = value;
      }
    });
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API request failed: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

// API client with HTTP methods
export const apiClient = {
  async get(endpoint: string, options?: RequestInit): Promise<Response> {
    const token = await getCurrentUserToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-organization-id': getActiveOrgId(),
      ...impersonateHeaders(),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      method: 'GET',
      headers: { ...headers, ...options?.headers },
    });
  },

  async post(endpoint: string, body: any, options?: RequestInit): Promise<Response> {
    const token = await getCurrentUserToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-organization-id': getActiveOrgId(),
      ...impersonateHeaders(),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      method: 'POST',
      headers: { ...headers, ...options?.headers },
      body: JSON.stringify(body),
    });
  },

  async patch(endpoint: string, body: any, options?: RequestInit): Promise<Response> {
    const token = await getCurrentUserToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-organization-id': getActiveOrgId(),
      ...impersonateHeaders(),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      method: 'PATCH',
      headers: { ...headers, ...options?.headers },
      body: JSON.stringify(body),
    });
  },

  async delete(endpoint: string, options?: RequestInit): Promise<Response> {
    const token = await getCurrentUserToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-organization-id': getActiveOrgId(),
      ...impersonateHeaders(),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      method: 'DELETE',
      headers: { ...headers, ...options?.headers },
    });
  },
};
